import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Row, Col, Form, Button, Spinner } from 'react-bootstrap';
import Layout from '../components/Layout';
import FormPageTitle from '../components/FormPageTitle';
import BackButton from '../components/BackButton';
import { promocodeTemplateService, PromocodeTemplate } from '../services/promocodeTemplateService';
import { fontService, Font } from '../services/fontService';
import CategoryMultiSelect from '../components/CategoryMultiSelect';
import FileDropzone from '../components/FileDropzone';
import { ImageAreaSelector, Area } from '../components/ImageAreaSelector';
import { API_BASE_URL, STORAGE_PATHS, DEFAULTS, ROUTES } from '../constants';
import { useTranslation } from '../contexts/LocaleContext';
import Slider from '@mui/material/Slider';
import { getGifDurationSeconds } from '../utils/gifDuration';

function formatTimeSeconds(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const PromocodeTemplateEdit: React.FC = () => {
  const t = useTranslation();
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isCreate = !id;
  const mediaRef = useRef<HTMLImageElement | HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const paragraphRef = useRef<HTMLDivElement>(null);

  const [template, setTemplate] = useState<PromocodeTemplate | null>(null);
  const [fonts, setFonts] = useState<Font[]>([]);
  const [loading, setLoading] = useState(true);
  const [imageNaturalSize, setImageNaturalSize] = useState<{ width: number; height: number } | null>(null);
  const [imageDisplaySize, setImageDisplaySize] = useState<{ width: number; height: number } | null>(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [videoMuted, setVideoMuted] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [dataImage, setDataImage] = useState<any>({
    color: DEFAULTS.PROMOCODE_TEMPLATE.COLOR,
    text_font_size: '',
    text_font: '',
    fontId: 0,
    angle: '',
    with_substrate: false,
    substrate_background_color: DEFAULTS.PROMOCODE_TEMPLATE.SUBSTRATE_BACKGROUND_COLOR,
    upper_radius: DEFAULTS.PROMOCODE_TEMPLATE.UPPER_RADIUS,
    sides_radius: DEFAULTS.PROMOCODE_TEMPLATE.SIDES_RADIUS,
    border_size: DEFAULTS.PROMOCODE_TEMPLATE.BORDER_SIZE,
    border_color: DEFAULTS.PROMOCODE_TEMPLATE.BORDER_COLOR,
    shadow_bold: DEFAULTS.PROMOCODE_TEMPLATE.SHADOW_BOLD,
    shadow_upper_size: DEFAULTS.PROMOCODE_TEMPLATE.SHADOW_UPPER_SIZE,
    shadow_sides_size: DEFAULTS.PROMOCODE_TEMPLATE.SHADOW_SIDES_SIZE,
    render_header: false,
    header_color: DEFAULTS.PROMOCODE_TEMPLATE.HEADER_COLOR,
    header_text: DEFAULTS.PROMOCODE_TEMPLATE.HEADER_TEXT,
    header_text_font: '',
    header_text_font_size: 0,
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    start_time: '',
    end_time: '',
  });

  useEffect(() => {
    loadFonts();
    if (id) {
      loadTemplate();
    } else {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!file) {
      setFilePreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setFilePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // При смене файла (режим создание) сбрасываем время и длительность видео
  useEffect(() => {
    if (!isCreate) return;
    setVideoDuration(0);
    setVideoCurrentTime(0);
    setVideoMuted(true);
    setDataImage((prev: any) => ({ ...prev, start_time: '', end_time: '' }));
  }, [file, isCreate]);

  const updateMediaDisplaySize = useCallback(() => {
    const el = mediaRef.current;
    if (!el) return;
    setImageDisplaySize({ width: el.offsetWidth, height: el.offsetHeight });
  }, []);

  useEffect(() => {
    const el = mediaRef.current;
    if (!el) return;
    const isVideo = el instanceof HTMLVideoElement;
    const onReady = () => {
      const w = isVideo ? (el as HTMLVideoElement).videoWidth : (el as HTMLImageElement).naturalWidth;
      const h = isVideo ? (el as HTMLVideoElement).videoHeight : (el as HTMLImageElement).naturalHeight;
      setImageNaturalSize({ width: w, height: h });
      setImageDisplaySize({ width: el.offsetWidth, height: el.offsetHeight });
      if (isVideo) {
        const dur = (el as HTMLVideoElement).duration;
        if (Number.isFinite(dur)) setVideoDuration(dur);
      }
    };
    if (isVideo) {
      const video = el as HTMLVideoElement;
      if (video.readyState >= 2) onReady();
      else video.addEventListener('loadedmetadata', onReady);
      const ro = new ResizeObserver(updateMediaDisplaySize);
      ro.observe(el);
      return () => {
        video.removeEventListener('loadedmetadata', onReady);
        ro.disconnect();
      };
    } else {
      const img = el as HTMLImageElement;
      const isGifEl = (file?.type === 'image/gif') || (template?.file?.mime_type?.includes('gif'));
      if (!isGifEl) setVideoDuration(0);
      const onReadyImg = () => {
        onReady();
        if (isGifEl) {
          const source = file ?? img.src;
          getGifDurationSeconds(source)
            .then((dur) => setVideoDuration(dur > 0 ? dur : 10))
            .catch(() => setVideoDuration(10));
        }
      };
      if (img.complete && img.naturalWidth) onReadyImg();
      else img.addEventListener('load', onReadyImg);
      const ro = new ResizeObserver(updateMediaDisplaySize);
      ro.observe(el);
      return () => {
        img.removeEventListener('load', onReadyImg);
        ro.disconnect();
      };
    }
  }, [template, file, filePreviewUrl, updateMediaDisplaySize]);

  useEffect(() => {
    if (videoDuration <= 0) return;
    setDataImage((prev: any) => {
      const start = Number(prev.start_time);
      const end = Number(prev.end_time);
      if (!Number.isFinite(start) || !Number.isFinite(end) || start < 0 || end <= 0) {
        return { ...prev, start_time: '0', end_time: String(videoDuration) };
      }
      return prev;
    });
  }, [videoDuration]);

  // Для GIF нет currentTime у <img> — эмулируем воспроизведение таймером (цикл 0 … duration)
  useEffect(() => {
    const isGifHere = (file?.type === 'image/gif') || (template?.file?.mime_type?.includes('gif') ?? false);
    if (!isGifHere || videoDuration <= 0) return;
    setVideoCurrentTime(0);
    const step = 0.05;
    const id = setInterval(() => {
      setVideoCurrentTime((prev) => {
        const next = prev + step;
        return next >= videoDuration ? 0 : next;
      });
    }, step * 1000);
    return () => clearInterval(id);
  }, [file, template?.file?.mime_type, videoDuration]);

  const applyCanvasStyles = useCallback((data: typeof dataImage) => {
    if (!canvasRef.current || !headerRef.current || !paragraphRef.current) return;
    const cnvs = canvasRef.current;
    const imageHeader = headerRef.current;
    const textParagraph = paragraphRef.current;
    const { x, y, width, height, with_substrate } = data;
    const angleDeg = Number(data.angle) || 0;

    imageHeader.style.position = 'absolute';
    imageHeader.style.zIndex = '130';
    imageHeader.style.color = data.header_color || '#000000';
    imageHeader.style.transform = `rotate(${angleDeg}deg)`;
    imageHeader.style.display = data.render_header ? '' : 'none';

    cnvs.style.transform = `rotate(${angleDeg}deg)`;
    cnvs.style.zIndex = '100';
    textParagraph.style.color = data.color || '#ffffff';

    if (width && height) {
      cnvs.style.display = 'flex';
      cnvs.style.width = width + 'px';
      cnvs.style.height = height + 'px';
      cnvs.style.left = x + 'px';
      cnvs.style.top = y + 'px';
      textParagraph.style.fontSize = width / 3 + 'px';
      imageHeader.style.fontSize = width / 12 + 'px';
      imageHeader.style.left = x + width / 4 + 'px';
      imageHeader.style.top = y - 50 + 'px';

      if (!with_substrate) {
        cnvs.style.background = 'transparent';
        cnvs.style.border = 'none';
        cnvs.style.boxShadow = 'none';
      } else {
        cnvs.style.background = data.substrate_background_color || '#ffffff';
        cnvs.style.borderRadius = `${data.upper_radius || 1}px / ${data.sides_radius || 1}px`;
        cnvs.style.border = `${data.border_size || 0}px solid ${data.border_color || '#000000'}`;
        cnvs.style.boxShadow = `-${data.shadow_upper_size || 10}px -${data.shadow_sides_size || 10}px 10px 1px`;
      }
    } else {
      cnvs.style.display = 'none';
    }
  }, []);

  // Синхронизация позиции и размера канваса/заголовка с областью выделения
  useEffect(() => {
    if (!template && !(isCreate && file)) return;
    applyCanvasStyles(dataImage);
  }, [template, dataImage, applyCanvasStyles, isCreate, file]);

  const loadTemplate = async () => {
    if (!id) return;
    try {
      const data = await promocodeTemplateService.getTemplate(parseInt(id));
      setTemplate(data);
      setName(data.name);
      if (data.category && data.category?.id !== null) {
        setSelectedCategories([data.category?.id]);
      } else if (data.categories?.length) {
        setSelectedCategories(data.categories.map((c: any) => (typeof c === 'object' && c?.id != null ? c.id : c)));
      }
      if (data.data) {
        const d = data.data;
        setDataImage((prev: any) => ({
          ...prev,
          ...d,
          color: d.textColor ?? d.color ?? prev.color,
          angle: d.rotationAngle ?? d.angle ?? prev.angle,
          start_time: d.startTimeSeconds ?? d.start_time ?? prev.start_time,
          end_time: d.endTimeSeconds ?? d.end_time ?? prev.end_time,
          text_font_size: d.fontSize ?? d.text_font_size ?? prev.text_font_size,
          fontId: d.fontId ?? prev.fontId,
          x: d.x ?? prev.x,
          y: d.y ?? prev.y,
          width: d.width ?? prev.width,
          height: d.height ?? prev.height,
        }));
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading template:', error);
      setLoading(false);
    }
  };


  const loadFonts = async () => {
    try {
      const list = await fontService.getFonts();
      setFonts(list);
    } catch (err) {
      console.error('Error loading fonts:', err);
    }
  };

  useEffect(() => {
    if (fonts.length > 0 && (!dataImage.fontId || Number(dataImage.fontId) === 0)) {
      setDataImage((prev: any) => ({
        ...prev,
        fontId: fonts[0].id,
        text_font: fonts[0].name,
      }));
    }
  }, [fonts, dataImage.fontId]);

  const scaleX = imageNaturalSize && imageDisplaySize && imageNaturalSize.width
    ? imageDisplaySize.width / imageNaturalSize.width
    : 1;
  const scaleY = imageNaturalSize && imageDisplaySize && imageNaturalSize.height
    ? imageDisplaySize.height / imageNaturalSize.height
    : 1;

  const handleAreaChange = (area: Area | null) => {
    if (!area) {
      setDataImage((prev: any) => ({ ...prev, x: 0, y: 0, width: 0, height: 0 }));
      return;
    }
    const naturalX = scaleX ? area.x / scaleX : area.x;
    const naturalY = scaleY ? area.y / scaleY : area.y;
    const naturalW = scaleX ? area.width / scaleX : area.width;
    const naturalH = scaleY ? area.height / scaleY : area.height;
    setDataImage((prev: any) => ({
      ...prev,
      x: naturalX,
      y: naturalY,
      width: naturalW,
      height: naturalH,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {

      const body = {
        name,
        file: file || undefined,
        x: Number(dataImage.x) || 0,
        y: Number(dataImage.y) || 0,
        width: Number(dataImage.width) || 0,
        height: Number(dataImage.height) || 0,
        fontId: Number(dataImage.fontId) || 0,
        textColor: dataImage.color || '',
        rotationAngle: Number(dataImage.angle) || 0,
        startTimeSeconds: Number(dataImage.start_time) || 0,
        endTimeSeconds: Number(dataImage.end_time) || 0,
        categoryId: selectedCategories[0],
      }

      if (isCreate && !file) {
        alert(t('validation.selectFile'));
        return;
      }
      if (selectedCategories.length === 0) {
        alert(t('validation.selectCategory'));
        return;
      }
      const w = Number(dataImage.width) || 0;
      const h = Number(dataImage.height) || 0;
      if (w <= 0 || h <= 0) {
        alert(t('validation.selectArea'));
        return;
      }
      if (!dataImage.fontId || Number(dataImage.fontId) === 0) {
        alert(t('validation.selectFont'));
        return;
      }

      setSaving(true);
      try {
        if (isCreate) {
          await promocodeTemplateService.createTemplate(body);
        } else if (id) {
          await promocodeTemplateService.updateTemplate(parseInt(id), body);
        }
        navigate(ROUTES.PROMOCODE_TEMPLATES);
      } finally {
        setSaving(false);
      }
    } catch (error) {
      console.error(isCreate ? 'Error creating template' : 'Error updating template', error);
      alert(isCreate ? t('errors.createTemplate') : t('errors.updateTemplate'));
      setSaving(false);
    }
  };

  const handleTest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    const form = e.target as HTMLFormElement;
    const text = (form.querySelector('input[name="text"]') as HTMLInputElement)?.value || 'code';
    window.open(`/promocode-template/test/${id}?text=${text}`, '_blank');
  };

  const handleDataImageChange = (key: string, value: any) => {
    setDataImage((prev: any) => {
      const newData = { ...prev, [key]: value };
      applyCanvasStyles(newData);
      return newData;
    });
  };

  const handleVideoTimeRangeChange = (_: Event, value: number | number[]) => {
    const [start, end] = value as number[];
    setDataImage((prev: any) => {
      const newData = { ...prev, start_time: String(start), end_time: String(end) };
      applyCanvasStyles(newData);
      return newData;
    });
  };

  if (id && (loading || !template)) {
    return (
      <Layout>
        <Row className="my-2">
          <Col xs={12}>
            <BackButton fallbackRoute={ROUTES.PROMOCODE_TEMPLATES} />
          </Col>
        </Row>
        <div className="d-flex justify-content-center my-4">
          <Spinner animation="border" role="status" />
        </div>
      </Layout>
    );
  }
  const fileSrc = template?.file ? (template.file.url ?? template.file.path) : undefined;
  const mediaUrl =
    filePreviewUrl ||
    (fileSrc
      ? (fileSrc.startsWith('http') ? fileSrc : `${API_BASE_URL}${fileSrc}`)
      : template?.thumbnail?.path
        ? `${template.thumbnail.path}`
        : '');
  const isVideo =
    (file != null && file.type.startsWith('video/')) ||
    (template?.type === 'video');
  const isGif =
    (file != null && file.type === 'image/gif') ||
    (template?.file?.mime_type?.includes('gif') ?? false);
  const showVideoControls = isVideo || isGif;
  const showImageBlock = template || (isCreate && file);
  const selectedFont = fonts.find((f) => f.id === dataImage.fontId);
  const paragraphFontClass = selectedFont
    ? `custom-font-${(selectedFont.name || '').replace(/[\s.]/g, '') || `font-${selectedFont.id}`}`
    : '';

  const displayedArea: Area | null =
    dataImage.width && dataImage.height && imageNaturalSize
      ? {
          x: dataImage.x * scaleX,
          y: dataImage.y * scaleY,
          width: dataImage.width * scaleX,
          height: dataImage.height * scaleY,
        }
      : null;

  const startSec = Number(dataImage.start_time) || 0;
  const endSec = dataImage.end_time === '' ? videoDuration : Number(dataImage.end_time);
  const showPromoOverlay =
    (!isVideo && !isGif) ||
    (videoCurrentTime >= startSec && videoCurrentTime <= endSec);

  const overlayWrapperStyle: React.CSSProperties =
    imageNaturalSize && imageDisplaySize
      ? {
          position: 'absolute',
          left: 0,
          top: 0,
          width: imageNaturalSize.width,
          height: imageNaturalSize.height,
          transform: `scale(${scaleX}, ${scaleY})`,
          transformOrigin: '0 0',
          pointerEvents: 'none',
          zIndex: 5,
          visibility: showPromoOverlay ? 'visible' : 'hidden',
        }
      : { position: 'absolute', left: 0, top: 0, pointerEvents: 'none', zIndex: 5, visibility: showPromoOverlay ? 'visible' : 'hidden' };

  return (
    <Layout>
      <style>
        {fonts
          .filter((font) => font.file?.path)
          .map((font) => {
            const fontName = (font.name || '').replace(/[\s.]/g, '') || `font-${font.id}`;
            const fontUrl = font.file!.path.startsWith('http')
              ? font.file!.path
              : `${API_BASE_URL}${font.file!.path}`;
            return `
            @font-face {
              font-family: "${fontName}";
              src: url("${fontUrl}");
            }
            .custom-font-${fontName} {
              font-family: "${fontName}";
            }
          `;
          })}
      </style>

      <Row className="my-2">
        <Col xs={12}>
          <BackButton fallbackRoute={ROUTES.PROMOCODE_TEMPLATES} />
        </Col>
      </Row>
      <FormPageTitle title={isCreate ? t('promocode.create') : t('promocode.edite')} />

      <Row>
        <Col xs={12} md={{ span: 8, offset: 2 }}>
          <Form onSubmit={handleSubmit} encType="multipart/form-data">
            <Form.Group className="mb-3">
              <Form.Label>{t('formField.name')}</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('formField.file')}</Form.Label>
              <FileDropzone value={file} onChange={(v) => setFile(Array.isArray(v) ? (v[0] ?? null) : v)} />
              {showImageBlock && mediaUrl && (
              <div id="general-container" className='d-flex align-items-center flex-column'>
                <div className="d-flex justify-content-start align-items-center py-2 w-100">
                  <p className="m-0">{t('formField.yourCurrentTemplate')}</p>
                </div>
                <div id="imageDiv" style={{ position: 'relative', display: 'inline-block', overflow: 'hidden'}}>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    {isVideo ? (
                      <video
                        id="media"
                        ref={mediaRef as React.RefObject<HTMLVideoElement>}
                        src={mediaUrl}
                        muted={videoMuted}
                        loop
                        playsInline
                        autoPlay
                        onTimeUpdate={(e) => setVideoCurrentTime((e.target as HTMLVideoElement).currentTime)}
                        style={{ maxWidth: '100%', height: 'auto', display: 'block', position: 'relative', zIndex: 0, maxHeight: '1000px', objectFit: 'contain' }}
                      />
                    ) : (
                      <img
                        id="image"
                        ref={mediaRef as React.RefObject<HTMLImageElement>}
                        src={mediaUrl}
                        alt=""
                        style={{ maxWidth: '100%', height: 'auto', display: 'block', position: 'relative', zIndex: 0, maxHeight: '1000px', objectFit: 'contain' }}
                      />
                    )}
                    <div style={overlayWrapperStyle}>
                      <div
                        id="imageHeader"
                        ref={headerRef}
                      style={{
                        position: 'absolute',
                        zIndex: 130,
                        pointerEvents: 'none',
                      }}
                    >
                      <p>{dataImage.header_text || 'PROMO-CODE'}</p>
                    </div>
                    <div
                      id="myCanvas"
                      ref={canvasRef}
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'absolute',
                        zIndex: 100,
                        pointerEvents: 'none',
                      }}
                    >
                      <div
                        id="paragraph"
                        ref={paragraphRef}
                        className={paragraphFontClass}
                        style={{
                          textAlign: 'center',
                          display: 'table-cell',
                          verticalAlign: 'middle',
                        }}
                      >
                        CODE
                      </div>
                    </div>
                    </div>
                    <ImageAreaSelector
                      value={displayedArea}
                      onChange={handleAreaChange}
                      onAngleChange={(angle) => handleDataImageChange('angle', String(angle))}
                      angle={Number(dataImage.angle) || 0}
                    />
                  </div>
                </div>
                {showVideoControls && videoDuration > 0 && (
                  <div className="mt-2 w-100 px-1">
                    <div className="d-flex justify-content-between small text-muted mb-1">
                      <span>{t('formField.startTime')}: {formatTimeSeconds(Number(dataImage.start_time) || 0)}</span>
                      <span>{t('formField.endTime')}: {formatTimeSeconds(dataImage.end_time === '' ? videoDuration : Number(dataImage.end_time))}</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      {isVideo && (
                        <Button
                          type="button"
                          variant="outline-secondary"
                          size="sm"
                          className="flex-shrink-0 me-3"
                          onClick={() => {
                            const el = mediaRef.current;
                            if (el instanceof HTMLVideoElement) {
                              el.currentTime = 0;
                              el.play();
                            }
                          }}
                          title={t('promocode.restartVideo')}
                          aria-label={t('promocode.restartVideo')}
                        >
                          <i className="bi bi-arrow-counterclockwise" aria-hidden />
                        </Button>
                      )}
                      <Slider
                        min={0}
                        max={videoDuration}
                        step={0.1}
                        value={[
                          Math.min(Number(dataImage.start_time) || 0, (Number(dataImage.end_time) ?? videoDuration) - 0.01),
                          Math.max(Number(dataImage.end_time) ?? videoDuration, Number(dataImage.start_time) || 0),
                        ]}
                        onChange={handleVideoTimeRangeChange}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => formatTimeSeconds(v)}
                        size="medium"
                        sx={{ flex: 1, minWidth: 0, mt: 0.5, mb: 0.5 }}
                      />
                      {isVideo && (
                        <Button
                          type="button"
                          variant="outline-secondary"
                          size="sm"
                          className="flex-shrink-0 ms-3"
                          onClick={() => setVideoMuted((m) => !m)}
                          title={videoMuted ? t('promocode.unmute') : t('promocode.mute')}
                          aria-label={videoMuted ? t('promocode.unmute') : t('promocode.mute')}
                        >
                          <i className={videoMuted ? 'bi bi-volume-mute' : 'bi bi-volume-up'} aria-hidden />
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('formField.categories')}</Form.Label>
              <CategoryMultiSelect
                value={selectedCategories}
                onChange={setSelectedCategories}
                height="auto"
                maxHeight="200px"
                mode="only"
              />
            </Form.Group>

            <Form.Label>{t('formField.imageParams')}</Form.Label>
            <div id="image-params">
              <Row>
                <Col xs={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('formField.textColor')}</Form.Label>
                    <Form.Control
                      type="color"
                      value={dataImage.color}
                      onChange={(e) => handleDataImageChange('color', e.target.value)}
                    />
                  </Form.Group>
                </Col>

                {
                  fonts?.length > 0 &&
                  
                  <Col xs={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('formField.textFont')}</Form.Label>
                      <Form.Select
                        value={dataImage.fontId ?? fonts[0]?.id ?? ''}
                        onChange={(e) => {
                          const id = Number(e.target.value);
                          const font = fonts.find((f) => f.id === id);
                          setDataImage((prev: any) => ({
                            ...prev,
                            fontId: id,
                            text_font: font?.name ?? '',
                          }));
                        }}
                      >
                        {fonts.map((font) => (
                          <option key={font.id} value={font.id}>
                            {font.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                }
                

                <Col xs={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('formField.angle')}</Form.Label>
                    <Form.Control
                      type="number"
                      value={dataImage.angle}
                      onChange={(e) => handleDataImageChange('angle', e.target.value)}
                    />
                  </Form.Group>
                </Col>

                <hr />

                {/* <h2>{t('formField.substrateSettings')}</h2>
                <Col xs={12}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label={t('formField.render')}
                      checked={dataImage.with_substrate}
                      onChange={(e) => {
                        handleDataImageChange('with_substrate', e.target.checked);
                        if (canvasRef.current) {
                          canvasRef.current.style.display = e.target.checked ? 'flex' : 'none';
                        }
                      }}
                    />
                  </Form.Group>
                </Col> */}

                {/* <div
                  id="substrate-settings"
                  style={{ display: dataImage.with_substrate ? 'block' : 'none' }}
                >
                  <Col xs={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('formField.bgColor')}</Form.Label>
                      <Form.Control
                        type="color"
                        value={dataImage.substrate_background_color}
                        onChange={(e) => handleDataImageChange('substrate_background_color', e.target.value)}
                      />
                    </Form.Group>
                  </Col>

                  <Row>
                    <Col xs={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('formField.upperRadius')}</Form.Label>
                        <Form.Control
                          type="number"
                          value={dataImage.upper_radius}
                          onChange={(e) => handleDataImageChange('upper_radius', parseInt(e.target.value))}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('formField.sidesRadius')}</Form.Label>
                        <Form.Control
                          type="number"
                          value={dataImage.sides_radius}
                          onChange={(e) => handleDataImageChange('sides_radius', parseInt(e.target.value))}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('formField.borderSize')}</Form.Label>
                        <Form.Control
                          type="number"
                          value={dataImage.border_size}
                          onChange={(e) => handleDataImageChange('border_size', parseInt(e.target.value))}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('formField.borderColor')}</Form.Label>
                        <Form.Control
                          type="color"
                          value={dataImage.border_color}
                          onChange={(e) => handleDataImageChange('border_color', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <h4>{t('formField.shadowSettings')}</h4>
                    <Col xs={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('formField.shadowBold')}</Form.Label>
                        <Form.Control
                          type="number"
                          value={dataImage.shadow_bold}
                          onChange={(e) => handleDataImageChange('shadow_bold', parseInt(e.target.value))}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('formField.shadowUpperSize')}</Form.Label>
                        <Form.Control
                          type="number"
                          value={dataImage.shadow_upper_size}
                          onChange={(e) => handleDataImageChange('shadow_upper_size', parseInt(e.target.value))}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('formField.shadowSidesSize')}</Form.Label>
                        <Form.Control
                          type="number"
                          value={dataImage.shadow_sides_size}
                          onChange={(e) => handleDataImageChange('shadow_sides_size', parseInt(e.target.value))}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <h4>{t('formField.headerSettings')}</h4>
                    <Col xs={12}>
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="checkbox"
                          label={t('formField.renderHeader')}
                          checked={dataImage.render_header}
                          onChange={(e) => handleDataImageChange('render_header', e.target.checked)}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('formField.headerColor')}</Form.Label>
                        <Form.Control
                          type="color"
                          value={dataImage.header_color}
                          onChange={(e) => handleDataImageChange('header_color', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('formField.headerText')}</Form.Label>
                        <Form.Control
                          type="text"
                          value={dataImage.header_text}
                          onChange={(e) => handleDataImageChange('header_text', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('formField.headerTextFont')}</Form.Label>
                        <Form.Select
                          value={dataImage.header_text_font || (fonts[0]?.name ?? '')}
                          onChange={(e) => handleDataImageChange('header_text_font', e.target.value)}
                        >
                          {fonts.map((font) => (
                            <option key={font.id} value={font.name}>
                              {font.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col xs={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('formField.headerTextFontSize')}</Form.Label>
                        <Form.Control
                          type="number"
                          value={dataImage.header_text_font_size}
                          onChange={(e) => handleDataImageChange('header_text_font_size', parseInt(e.target.value))}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {(template.type === 'video' || template.file?.mime_type?.includes('gif')) && (
                  <Row>
                    <Col xs={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('formField.startTime')}</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.001"
                          value={dataImage.start_time}
                          onChange={(e) => handleDataImageChange('start_time', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('formField.endTime')}</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.001"
                          value={dataImage.end_time}
                          onChange={(e) => handleDataImageChange('end_time', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )} */}

              </Row>
            </div>

            <Button variant="primary" type="submit" className="w-100" disabled={saving}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" aria-hidden />
                  <span>{t('manage.saving')}</span>
                </>
              ) : (
                t('manage.save')
              )}
            </Button>
          </Form>
        </Col>
      </Row>
    </Layout>
  );
};

export default PromocodeTemplateEdit;
